import React, { type ErrorInfo, lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { di } from 'react-magnetic-di';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { failUfoExperience, startUfoExperience } from '../../state/analytics';
import { importWithRetry } from '../../utils';
import { useSmartLinkAnalyticsContext } from '../../utils/analytics/SmartLinkAnalyticsContext';
import { isFlexibleUiCard } from '../../utils/flexible';
import { clearMarks, clearMeasures } from '../../utils/performance';
import { type CardProps } from '../Card/types';

import { LoadingCardLink } from './component-lazy/LoadingCardLink';
import { type CardWithUrlContentProps } from './types';
export const LazyCardWithUrlContent: React.LazyExoticComponent<
	typeof import('./component-lazy/index').default
> = lazy(() =>
	importWithRetry(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_smartcard-urlcardcontent" */ './component-lazy/index'
			),
	),
);

export function CardWithURLRenderer(props: CardProps): React.JSX.Element {
	di(LazyCardWithUrlContent);
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const [id] = useState(() => (props.id ? props.id : uuid()) satisfies string);
	const { fireEvent } = useAnalyticsEvents();

	useEffect(() => {
		// ComponentWillUnmount
		return () => {
			clearMarks(id);
			clearMeasures(id);
		};
	}, [id]);

	const {
		url,
		appearance,
		isSelected,
		isHovered,
		frameStyle,
		onClick,
		container,
		onResolve,
		onError,
		testId,
		actionOptions,
		inheritDimensions,
		platform,
		embedIframeRef,
		embedIframeUrlType,
		inlinePreloaderStyle,
		children,
		ui,
		showHoverPreview,
		hoverPreviewOptions,
		placeholder,
		fallbackComponent,
		removeTextHighlightingFromTitle,
		resolvingPlaceholder,
		truncateInline,
		CompetitorPrompt,
		disablePreviewPanel,
	} = props;

	const isFlexibleUi = isFlexibleUiCard(children, ui);

	const analyticsContext = useSmartLinkAnalyticsContext({
		display: isFlexibleUi ? 'flexible' : appearance,
		id,
		url: url ?? '',
	});

	const errorHandler = useCallback(
		(error: Error, info: ErrorInfo) => {
			const { componentStack } = info;
			const errorInfo: ErrorInfo = {
				componentStack,
			};
			// NB: APIErrors are thrown in response to Object Resolver Service. We do not
			// fire an event for these, as they do not cover failed UI render events.
			// The rest of the errors caught here are unexpected, and correlate
			// to the reliability of the smart-card front-end components.
			// Likewise, chunk loading errors are not caused by a failure of smart-card rendering.
			if (error.name === 'ChunkLoadError') {
				fireEvent('operational.smartLink.chunkLoadFailed', {
					...analyticsContext?.attributes,
					display: appearance,
					error: error as any,
					errorInfo: errorInfo as any,
					definitionId: null,
				});
			} else if (error.name !== 'APIError') {
				startUfoExperience('smart-link-rendered', id || 'NULL');
				failUfoExperience('smart-link-rendered', id || 'NULL');
				failUfoExperience('smart-link-authenticated', id || 'NULL');
				fireEvent('ui.smartLink.renderFailed', {
					...analyticsContext?.attributes,
					display: isFlexibleUi ? 'flexible' : appearance,
					id: id ?? null,
					error: error as any,
					errorInfo: errorInfo as any,
				});
			}
			onError && onError({ status: 'errored', url: url ?? '', err: error });
		},
		[analyticsContext?.attributes, appearance, fireEvent, id, isFlexibleUi, onError, url],
	);

	if (!url) {
		throw new Error('@atlaskit/smart-card: url property is missing.');
	}

	const defaultFallBackComponent = () => null;
	const FallbackComponent = fallbackComponent ?? defaultFallBackComponent;
	const ErrorFallback = () => <FallbackComponent />;

	// FlexibleCards always need full ORS data regardless of appearance prop,
	// because they render custom blocks (TitleBlock etc.) requiring complete response.
	// Override appearance to 'block' for all FlexibleCards when FG is enabled.
	const effectiveAppearance =
		isFlexibleUi && fg('platform_smartlink_inline_resolve_optimization')
			? 'block'
			: appearance;

	const cardWithUrlProps: CardWithUrlContentProps = {
		id,
		url,
		appearance: effectiveAppearance,
		onClick,
		isSelected,
		isHovered,
		frameStyle,
		container,
		onResolve,
		onError,
		testId,
		actionOptions,
		inheritDimensions,
		platform,
		embedIframeRef,
		embedIframeUrlType,
		inlinePreloaderStyle,
		ui,
		showHoverPreview,
		hoverPreviewOptions,
		placeholder,
		removeTextHighlightingFromTitle,
		resolvingPlaceholder,
		truncateInline,
		CompetitorPrompt,
		disablePreviewPanel,
	};

	return (
		<ErrorBoundary FallbackComponent={ErrorFallback} onError={errorHandler}>
			<Suspense fallback={<LoadingCardLink {...cardWithUrlProps} />}>
				<LazyCardWithUrlContent {...cardWithUrlProps}>{children}</LazyCardWithUrlContent>
			</Suspense>
		</ErrorBoundary>
	);
}
